import { FC } from 'react';
import { CardContent } from '@mui/material';
import { Hooks } from '@wellstone-solutions/common';
import { useTranslation } from 'react-i18next';

import { Alert } from '@components/Alert';
import { CardHeader } from '@components/ui/Card';
import { PasswordField } from '@components/ui/Form';
import { ResetPasswordForm } from '@modules/auth/components/ResetPasswordForm';
import { RESET_PASSWORD_FORM_ID } from '@modules/auth/constants';
import {
  ResetPasswordSchema,
  ResetPasswordSchemaType,
} from '@modules/auth/schemas/reset-password-schema';
import { resolveErrorAlert } from '@utils/translate-from-map';

import type { ResetPasswordMutationResult } from './types';
import type { TranslationMapType } from '@utils/translate-from-map';

const ALERTS: TranslationMapType = {
  default: 'auth.forgot_password.default_alert',
  detail: 'auth.forgot_password.default_alert',
  token: 'auth.forgot_password.default_alert',
  password: 'auth.reset_password.password_repeated',
};

type ResetPasswordType = ReturnType<
  typeof Hooks.useForm<ResetPasswordSchemaType>
>;
type MutationProps = {
  mutation: ResetPasswordMutationResult;
};

const ResetPasswordInitial: FC<MutationProps> = ({ mutation }) => {
  const { t } = useTranslation();
  return (
    <>
      <CardHeader title={t('auth.reset_password.title')} />
      <CardContent sx={styles.cardContent}>
        {mutation.isError && (
          <Alert severity="error">
            {mutation.error?.response?.data
              ? resolveErrorAlert(t, mutation.error, ALERTS)
              : t(ALERTS.default)}
          </Alert>
        )}
        <ResetPasswordForm
          formId={RESET_PASSWORD_FORM_ID}
          schema={ResetPasswordSchema}
          initialValues={{ password: '', passwordConfirmation: '' }}
          mutation={mutation}
          mutateOptions={{
            onError: (error) => {
              console.error('ResetPassword failed:', error);
            },
          }}>
          {(form) => {
            const formik = form as ResetPasswordType;

            const passwordConfirmed = () => {
              if (formik.errors.password || formik.errors.passwordConfirmation)
                return false;

              return (
                formik.values.password &&
                formik.values.passwordConfirmation &&
                formik.values.password === formik.values.passwordConfirmation
              );
            };

            const passwordFieldHelperText = () => {
              if (!formik.touched.password) {
                return t('auth.signup.password_helper_text');
              }

              if (formik.errors.password) return formik.errors.password;

              return t('auth.signup.password_success');
            };

            const passwordConfirmationFieldHelperText = () => {
              if (!formik.touched.passwordConfirmation) {
                return t('auth.signup.password_confirmation_helper_text');
              }

              if (formik.errors.passwordConfirmation)
                return formik.errors.passwordConfirmation;

              return t('auth.signup.password_confirmation_success');
            };

            return (
              <>
                <PasswordField
                  fullWidth
                  formField="password"
                  label={t('auth.password')}
                  id="password"
                  inputProps={{ maxLength: 128 }}
                  onChange={formik.handleFieldChange('password')}
                  value={formik.values.password}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={passwordFieldHelperText()}
                  sx={passwordConfirmed() ? styles.success : undefined}
                />
                <PasswordField
                  fullWidth
                  formField="passwordConfirmation"
                  label={t('auth.signup.password_confirmation')}
                  id="password-confirmation"
                  inputProps={{ maxLength: 128 }}
                  onChange={formik.handleFieldChange('passwordConfirmation')}
                  value={formik.values.passwordConfirmation}
                  error={
                    formik.touched.passwordConfirmation &&
                    Boolean(formik.errors.passwordConfirmation)
                  }
                  helperText={passwordConfirmationFieldHelperText()}
                  sx={passwordConfirmed() ? styles.success : undefined}
                />
              </>
            );
          }}
        </ResetPasswordForm>
      </CardContent>
    </>
  );
};

const styles = {
  success: {
    '& .MuiFormHelperText-root': {
      color: 'success.main',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
};

export { ResetPasswordInitial };
